import DialogView from '../../dialog/Dialog.view';
import styles from './MarketComponentEditDialog.desktop.module.css';
import { useTranslation } from 'react-i18next';
import { useMarketComponentEditDialogController } from '../MarketComponentEditDialog.controller';
import canDeleteMarketComponent from '../../../security/CanDeleteMarketComponent';
import SmartSelectView from '../../../components/smart-select/SmartSelect.view';
import InputView from '../../../components/input/Input.view';
import DatePickerView from '../../../components/date-picker/DatePicker.view';
import { getMaxDaysBeforeSwitchToRed, getMaxStatusDays } from '../../../tools/ConfigurationReader';
import type MarketComponentEditDialogProperties from '../MarketComponentEditDialog.properties.ts';
import type { MarketComponentArticleTypeMarketComponentArticleTypeOutput } from '../../../generated/fresh-guard-api';
import LoaderView from '../../../components/loader/Loader.view.tsx';
import React from 'react';
import type { SelectType } from '../../../components/shared/select/Select.type.ts';

export default function MarketComponentEditDialogDesktopView(
  props: MarketComponentEditDialogProperties
) {
  const { t } = useTranslation();
  const {
    orientation,
    number,
    shortDescription,
    marketComponentArticleTypes,
    selectedValue,
    switchToYellowAfterXDays,
    switchToRedAfterXDays,
    switchToRedAfterDate,
    daysBeforeSwitchToRed,
    loading,
    handleChange,
    editSwitchToYellowAfterXDays,
    editSwitchToRedAfterXDays,
    editSwitchToRedAfterDate,
    editDaysBeforeSwitchToRed,
    setShortDescription,
    handleNumberChange,
    editOrientation,
    handleSaveClick,
    handleDeleteClick,
  } = useMarketComponentEditDialogController(props);

  return (
    <DialogView
      header={props.marketComponent?.shortDescription ?? ''}
      onBackdrop={props.handleDialogCloseClick}
    >
      <>
        {loading && <LoaderView />}

        {!loading && (
          <>
            <table className={styles.table}>
              <tbody>
                <tr>
                  <td className={styles.tdKey}>{t('ui.marketComponentNumberLabel')}</td>
                  <td className={styles.tdValue}>
                    <InputView
                      value={number}
                      type="number"
                      min="1"
                      max="9999"
                      onChange={(e) => handleNumberChange(e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className={styles.tdKey}>{t('ui.rotateLabel')}</td>
                  <td className={styles.tdValue}>
                    <InputView
                      value={orientation}
                      type="number"
                      min="0"
                      max="359"
                      onChange={(e) => editOrientation(e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className={styles.tdKey}>{t('ui.shortDescriptionLabel')}</td>
                  <td className={styles.tdValue}>
                    <InputView
                      value={shortDescription}
                      onChange={(e) => setShortDescription(e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className={styles.tdKey}>{t('ui.articleTypeLabel')}</td>
                  <td className={styles.tdValue}>
                    <SmartSelectView
                      data={marketComponentArticleTypes.map(
                        (articleType: MarketComponentArticleTypeMarketComponentArticleTypeOutput) =>
                          ({
                            label: articleType.name,
                            value: articleType.id?.toString(),
                          }) as SelectType
                      )}
                      selectedValue={selectedValue?.toString() ?? ''}
                      handleChange={handleChange}
                    />
                  </td>
                </tr>
                <tr>
                  <td className={styles.tdKey}>{t('ui.switchToYellowAfterXDays')}</td>
                  <td className={styles.tdValue}>
                    <InputView
                      value={switchToYellowAfterXDays}
                      type="number"
                      min="0"
                      max={getMaxStatusDays().toString()}
                      onChange={(e) => editSwitchToYellowAfterXDays(e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className={styles.tdKey}>{t('ui.switchToRedAfterXDays')}</td>
                  <td className={styles.tdValue}>
                    <InputView
                      value={switchToRedAfterXDays}
                      type="number"
                      min="0"
                      max={getMaxStatusDays().toString()}
                      onChange={(e) => editSwitchToRedAfterXDays(e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td className={styles.tdKey}>{t('ui.switchToRedAfterDate')}</td>
                  <td className={styles.tdValue}>
                    <DatePickerView
                      value={switchToRedAfterDate ? new Date(switchToRedAfterDate) : null}
                      placeholder={t('ui.switchToRedAfterDatePlaceholder')}
                      dateFormat={t('date.short')}
                      onChange={editSwitchToRedAfterDate}
                      switchToRedAfterXDays={props.marketComponent?.switchToRedAfterXDays}
                      daysBeforeSwitchToRed={props.marketComponent?.daysBeforeSwitchToRed}
                    />
                  </td>
                </tr>
                <tr>
                  <td className={styles.tdKey}>{t('ui.daysBeforeSwitchToRed')}</td>
                  <td className={styles.tdValue}>
                    <InputView
                      value={daysBeforeSwitchToRed}
                      type="number"
                      min="0"
                      max={getMaxDaysBeforeSwitchToRed().toString()}
                      onChange={(e) => editDaysBeforeSwitchToRed(e.target.value)}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <div className={styles.buttonContainer}>
              {canDeleteMarketComponent() && (
                <button className={styles.buttonDelete} onClick={handleDeleteClick}>
                  {t('ui.delete')}
                </button>
              )}
              <button className={styles.buttonSave} onClick={handleSaveClick}>
                {t('ui.save')}
              </button>
            </div>
          </>
        )}
      </>
    </DialogView>
  );
}
